/**
 * event-attendance.js
 */

import React from 'react';
import { Chip, Popover, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { parseISO } from 'date-fns';
import { getAttendance } from 'utils/event-attendance';
import useRecordStore from 'contexts/record-store/record-store';
import FeatureBox from 'components/feature-box';
import Icon from 'components/icon';


const useStyles = makeStyles(theme => ({
  controlRoot: {
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none',
    '& .optional': {
      pointerEvents: 'auto',
      '&.Mui-disabled': {
        pointerEvents: 'none',
      },
      '&:has(div.Mui-disabled)': {
        pointerEvents: 'none',
      },
    },
    '& > *': {
      display: 'flex'
    },
  },
  icon: {
    '&.attending': {
      color: theme.palette.success.dark,
    },
    '&.not-attending': {
      color: theme.palette.warning.main
    },
    '&.undecided': {
      color: theme.palette.grey.colors[6]
    }
  },
  label: {
    marginLeft: theme.spacing(1),
    position: 'relative',
    bottom: -1,
  },
  chip: {
    color: 'inherit',
    borderColor: 'inherit',
    pointerEvents: 'none',
    '& .MuiChip-icon': {
      color: 'inherit',
      width: '14px',
      height: '14px',
      display: 'none',
    },
    '&.optional': {
      position: 'relative',
      pointerEvents: 'auto',
      cursor: 'pointer',
      transition: `150ms ease-in-out`,
      '&:hover': {
        color: theme.palette.primary.colors[0],
        backgroundColor: theme.palette.grey.colors[10],
      },
      '&.undecided': {
        color: theme.palette.primary.colors[0],
        backgroundColor: theme.palette.secondary.colors[6],
        '&:hover': {
          backgroundColor: theme.palette.secondary.colors[5]
        }
      },
    },
    '& .edit': {
      height: 18,
      width: 18,
      position: 'relative',
      marginLeft: 6,
      marginBottom: -4
    },
  },
  toggle: {
    display: 'flex',
    backgroundColor: theme.palette.grey.colors[10],
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      paddingBottom: theme.spacing(0.5),
      paddingTop: theme.spacing(0.5),
      fontWeight: 'normal',
      color: theme.palette.grey.colors[1],
      '& .MuiSvgIcon-root': {
        color: theme.palette.secondary.colors[4],
      },
      '&.Mui-selected': {
        backgroundColor: theme.palette.secondary.colors[4],
        '& .MuiSvgIcon-root': {
          color: theme.palette.grey.colors[9],
        }
      },
    }
  }
}));


export const AttendanceIcon = (props) => {
  const {
    isOptional,
    isAttending,
    onClick,
    label,
    className,
    showTooltip,
    ...rest
  } = props;
  const classes = useStyles();
  const attendance = getAttendance(isOptional, isAttending);
  

  const iconBase = (
    <>
      <Icon 
        type={attendance.iconName} 
        className={`${className || classes.icon} ${attendance.id}`} 
        onClick={onClick}
        {...rest}
      />
      {label &&
      <Typography className={classes.label}>
        {label}
      </Typography>}
    </>
  );

  return showTooltip
    ? (
      <Tooltip
        arrow
        placement='left'
        title={attendance.label}
      >
        <span className='flex-hc'>
          {iconBase}
        </span>
      </Tooltip>
    )
    : iconBase;
}


export const AttendanceChip = (props) => {
  const {
    isOptional,
    isAttending,
    endDate,
    onClick,
    disabled,
  } = props;
  const classes = useStyles();
  const attendance = getAttendance(isOptional, isAttending, endDate);

  return (
    <>
      <Chip 
        className={
          `${classes.chip} `
          + `${isOptional ? 'optional ' : ''}`
          + `${attendance.id} `}
        variant={isOptional && isAttending === null ? 'filled' : 'outlined'}
        size='small'
        clickable={false}
        onClick={onClick}
        label={<>
          {attendance.label}
          {isOptional &&
          <Icon type='edit' className='edit' />}
        </>}
        icon={<Icon type={attendance.iconName} />}
        disabled={disabled}
      />
    </>
  );
}


export const AttendanceControl = (props) => {
  const {
    eventRecord,
    disabled,
    tooltip
  } = props;
  const classes = useStyles();
  const recordStore = useRecordStore();
  const [anchor, setAnchor] = React.useState();
  const [loading, setLoading] = React.useState(false);

  if (!eventRecord.membership.get()) return null;

  const isOptional = eventRecord.membership.isOptional.get();
  const isAttending = eventRecord.membership.isAttending.get();
  const endDate = parseISO(eventRecord.endDate.get() || eventRecord.date.get());
  const isEventOver = endDate < new Date();
  const onClick = e => setAnchor(e.currentTarget);


  const onChangeAttendance = async (val) => {
    if (val !== null) {
      setLoading(true);
      await recordStore.setAttendance(val, eventRecord.id.get());
      setLoading(false);
    }
    setTimeout(() => setAnchor(), 500);
  }


  const attendanceProps = {
    isOptional,
    isAttending,
    endDate,
    disabled
  }


  return (
    <div className={`${classes.controlRoot} attendance-control`}>

      {/* chip (non-xs) */}
      <Tooltip
        arrow
        placement='top'
        title={tooltip || 'Click to change your attendance at this event'}
      >
        <div 
          className={`${isOptional ? 'optional' : ''}`} 
          onClick={disabled ? null : onClick}
        >
          <AttendanceChip {...attendanceProps} />
        </div>
      </Tooltip>

      {/* popover */}
      {isOptional &&
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor()}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
      >
        <FeatureBox loading={loading}>
          <ToggleButtonGroup
            exclusive
            value={isAttending}
            className={classes.toggle}
            onChange={(e, v) => onChangeAttendance(v)}
          >
            {/* not attending */}
            <Tooltip
              arrow
              placement='top'
              title={`${isAttending === null || isAttending 
                ? 'Click to not attend' 
                : isEventOver
                  ? `You're not attending`
                  : `You did not attend`}`}
            >
              <ToggleButton value={false} className={isAttending === false ? 'Mui-selected' : ''}>
                <AttendanceIcon isOptional={true} isAttending={false} />
              </ToggleButton>
            </Tooltip>

            {/* attending */}
            <Tooltip
              arrow
              placement='top'
              title={`${isAttending === null || !isAttending 
                ? 'Click to attend' 
                : isEventOver
                  ? `You attended`
                  : `You're attending`}`}
            >
              <ToggleButton value={true} className={isAttending ? 'Mui-selected' : ''}>
                {/* Attending */}
                <AttendanceIcon isOptional={true} isAttending={true} />
              </ToggleButton>
            </Tooltip>

          </ToggleButtonGroup>
        </FeatureBox>
      </Popover>}

    </div>
  );
}